import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { homeFor } from "@/components/RequireRole";
import { PictureGrid } from "@/components/kids/PictureGrid";
import { KidAvatar } from "@/components/live/KidAvatar";

const KEY = "cl_family_code";

export default function KidLogin() {
  const { login } = useProfile();
  const nav = useNavigate();
  const [code, setCode] = useState(localStorage.getItem(KEY) || "");
  const [fam, setFam] = useState(null);
  const [kid, setKid] = useState(null);
  const [pics, setPics] = useState([]);
  const [busy, setBusy] = useState(false);
  const find = async (c = code) => {
    setBusy(true);
    try { setFam((await http.get(`/auth/family/${encodeURIComponent(c.trim())}`)).data); localStorage.setItem(KEY, c.trim().toUpperCase()); }
    catch (e) { toast.error(formatApiError(e)); setFam(null); } finally { setBusy(false); }
  };
  useEffect(() => {
    const qr = new URLSearchParams(window.location.search).get("qr");
    if (!qr) { if (code) find(code); return; }
    http.get(`/auth/kid-qr/${encodeURIComponent(qr)}`).then(({ data }) => {
      localStorage.setItem(KEY, data.family_code); setCode(data.family_code);
      setFam({ family: data.family, pictures: data.pictures, kids: [data.kid] }); setKid(data.kid);
    }).catch((e) => { toast.error(formatApiError(e)); if (code) find(code); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pics.length !== 3) return;
    setBusy(true);
    login(null, null, { family_code: code, username: kid.username, pictures: pics })
      .then((u) => nav(homeFor(u.role, u.learner_type)))
      .catch((e) => { toast.error(formatApiError(e)); setPics([]); })
      .finally(() => setBusy(false));
  }, [pics]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="max-w-xl mx-auto px-6 py-14" data-testid="kid-login-page">
      <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C87D53]">Kids sign in</span>
      {!fam ? (
        <form onSubmit={(e) => { e.preventDefault(); find(); }} className="mt-3">
          <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E]">Hi there!</h1>
          <p className="mt-3 text-stone-600">Type your family code. Your grown-up can find it on their parent dashboard.</p>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={12} required placeholder="FAMILY CODE" data-testid="kid-family-code-input"
            className="mt-6 w-full px-5 py-4 rounded-2xl bg-white border border-[#D6CDB4] font-display text-3xl tracking-[0.3em] text-center text-[#2C4A3E] focus:outline-none focus:border-[#2C4A3E]" />
          <button disabled={busy} data-testid="kid-family-code-submit" className="mt-4 w-full py-4 rounded-full bg-[#2C4A3E] text-white text-lg inline-flex justify-center items-center gap-2">{busy && <Loader2 className="w-5 h-5 animate-spin" />}Next</button>
        </form>
      ) : !kid ? (
        <div className="mt-3">
          <h1 className="font-display text-4xl text-[#2C4A3E]">Who's learning today?</h1>
          <div className="mt-6 grid grid-cols-2 gap-3">{fam.kids.map((k) => (
            <button key={k.username} onClick={() => setKid(k)} data-testid={`kid-pick-${k.username}`} className="rounded-3xl bg-white border border-[#E6DEC3] p-5 flex flex-col items-center gap-2 hover:border-[#2C4A3E] transition-colors">
              <KidAvatar a={k.avatar} className="w-16 h-16" /><span className="font-display text-2xl text-[#2C4A3E]">{k.first_name}</span>
            </button>))}</div>
          {!fam.kids.length && <p className="mt-6 text-stone-500" data-testid="kid-none">No picture logins set up yet. Ask your grown-up to add one.</p>}
          <button onClick={() => { setFam(null); localStorage.removeItem(KEY); }} data-testid="kid-change-family" className="mt-6 text-sm text-stone-500 underline">Not your family?</button>
        </div>
      ) : (
        <div className="mt-3">
          <button onClick={() => { setKid(null); setPics([]); }} data-testid="kid-back" className="inline-flex items-center gap-1 text-sm text-stone-500"><ArrowLeft className="w-4 h-4" /> Back</button>
          <h1 className="font-display text-4xl text-[#2C4A3E] mt-2">Hi {kid.first_name}! Tap your 3 pictures</h1>
          <div className="mt-6">{busy ? <Loader2 className="w-8 h-8 animate-spin text-[#2C4A3E]" /> : <PictureGrid value={pics} onChange={setPics} testId="kid-pic" big />}</div>
        </div>
      )}
      <p className="mt-10 text-sm text-stone-500">Grown-ups and older students: <Link to="/login" className="text-[#C87D53]" data-testid="kid-to-email-login">sign in with email</Link></p>
    </div>
  );
}
